<template>
  <UiModalOrDropdown
    ref="modalOrDropdown"
    design-head="min-h-[67px] min-w-[350px] py-4 px-4"
    custom-design-footer="pb-4 px-4"
    design="min-w-[350px] py-4 px-4"
    :modal-focus="true"
    :close-with-history-back="false"
    @update:is-open="updateDropdownState"
    @shown="modalOrDropdownShown()"
    @hidden="modalOrDropdownHidden"
  >
    <template #trigger>
      <div>
        <div
          role="button"
          tabindex="0"
          :aria-label="t('select_several')"
          :aria-expanded="isDropdownOpen"
          :class="[
            {'flex items-center focus:outline-transparent focus:outline-offset-0 select-none relative pl-11 pr-6 pt-6 pb-1 font-semibold text-lg bg-white text-left w-full min-h-[62px] focus:outline-hidden custom-select-fontsize cursor-pointer' : ruesSearchbar || searchresultSearchbar},
            {'active-input-searchresult rounded-b-xl lg:pt-5 lg:min-h-[46px]' : searchresultSearchbar},
            {'active-input-rues' : ruesSearchbar},
            {'active-input focus:outline-petrol-500 focus:outline-offset-0 select-none relative pl-11 pr-6 pt-6 pb-1 font-semibold text-lg border border-neutral-200 bg-white rounded-xl text-left w-full min-h-[55px] focus:outline-hidden custom-select-fontsize cursor-pointer' : !ruesSearchbar && !searchresultSearchbar}
          ]"
          @click="modalOrDropdown.toggle"
          @keydown.enter="modalOrDropdown.toggle"
        >
          <span
            :class="[
              {'mt-[0.4rem] top-[0.1rem] lg:mt-0 lg:top-0': searchresultSearchbar},
              {'mt-[0.4rem] top-[0.1rem]': !searchresultSearchbar},
              'absolute text-lg scale-[80%] text-neutral-900 font-normal -left-[0.25rem] ml-11'
              ]"
          >
            {{ t('guests') }}
          </span>
          <span :class="['absolute top-0 left-0 py-2 h-full flex items-center ml-3']">
            <UiIcon src="/svg/icons/guests.svg" width="19" height="17" class="stroke-neutral-900" />
          </span>
          <span class="break-all line-clamp-1">
            {{ t(`personen.${numerus(params.personen)}`, { personen: params.personen }) }}
          </span>
          <span v-if="!ruesSearchbar && !searchresultSearchbar" class="absolute top-0 right-0 py-2 mr-3 h-full flex items-center">
            <UiIcon src="/svg/icons/select-arrow-uiselect.svg" width="12" height="12" class="fill-neutral-500 custom-select-guests" />
          </span>
        </div>
      </div>
    </template>
    <template #head />
    <template #default>
      <div class="grid gap-x-5 gap-y-5">
        <div class="grid grid-flow-col auto-cols-max place-content-between place-items-center gap-2 text-left text-base">
          <div class="flex items-center min-h-[24px]">
            <span class="h-[24px] w-[22px] mr-2 flex items-center justify-center">
              <UiIcon src="/svg/icons/guests.svg" width="19" height="17" class="stroke-neutral-900 inline-block" />
            </span>
            <span>{{ t('guests_quantity') }}</span>
          </div>
          <UiQuantityChanger :min="1" :max="99" :quantity-type="t('guests_quantity')" :model-value="params.personen" @update:model-value="updateNumber('personen', $event)" />
        </div>
        <div class="grid grid-flow-col auto-cols-max place-content-between place-items-center gap-2 text-left text-base">
          <div class="flex items-center">
            <span class="h-[24px] w-[22px] mr-2 flex items-center justify-center">
              <UiIcon src="/svg/icons/bedrooms.svg" width="18" height="18" class="fill-neutral-900 inline-block" />
            </span>
            <span>{{ t2('quantity_of_bedrooms') }}</span>
          </div>
          <UiQuantityChanger :quantity-type="t2('quantity_of_bedrooms')" :model-value="params.einzelzimmer" @update:model-value="updateNumber('einzelzimmer', $event)" />
        </div>
        <fieldset v-if="!isCurrentPortalHundeLodge()" class="flex justify-start border-0">
          <legend class="hidden">
            {{ t2('pets_allowed') }}
          </legend>
          <UiCheckbox
            :value="['kleintiere_erlaubt', 'katzen_erlaubt', 'hunde_alle_erlaubt', 'hunde_kleine_erlaubt']"
            :deactivate-check="params.ausstattung_haustiere?.includes('haustiere_nicht_erlaubt')"
            :model-value="params.ausstattung_haustiere"
            label-position="center"
            design-label="pl-2 text-base"
            @update:model-value="update('ausstattung_haustiere', $event)"
          >
            <template #label>
              {{ t2('pets_allowed') }}
            </template>
          </UiCheckbox>
        </fieldset>
      </div>
    </template>
    <template #footerClose>
      <div class="px-3 md:px-0 pb-3 md:pb-0">
        <UiButton color="petrol" buttonpadding="w-full md:w-auto px-4 py-3">
          {{ t('apply') }}
        </UiButton>
      </div>
    </template>
  </UiModalOrDropdown>
</template>

<script setup lang="ts">
import type { SearchFilterGuestsBedroomsPetsUpdatePayload } from '~/types/events/SearchFilterGuestsBedroomsPetsUpdatePayload'
import type { DesignVariant } from '~/types/ui/DesignVariant'
import { isDesignVariant } from '~/types/ui/DesignVariant'

const { isCurrentPortalHundeLodge } = usePortals()
const { translate: t, numerus } = useTranslations('SearchFilterGuests')
const t2 = useTranslations('SearchFilters').translate

const emit = defineEmits<{
  update: [value: SearchFilterGuestsBedroomsPetsUpdatePayload]
  shown: []
  hidden: [reason?: string]
}>()

const modalOrDropdown = ref()
const isDropdownOpen = ref(false)

const updateDropdownState = (newState) => {
  isDropdownOpen.value = newState
}

const props = defineProps({
  params: {
    type: Object,
    default: () => ({})
  },
  results: {
    type: Object,
    default: null
  },
  active: {
    type: Boolean,
    default: false
  },
  design: {
    type: String as PropType<DesignVariant>,
    default: '',
    validator: (value: string) => isDesignVariant(value)
  }
})

const ruesSearchbar = computed(() => props.design === 'ruesSearchbar')
const searchresultSearchbar = computed(() => props.design === 'searchresultSearchbar')
const modalOrDropdownShown = () => {
  emit('shown')
}

const modalOrDropdownHidden = (reason?: string) => {
  emit('hidden', reason)
}
const update = (key, val) => {
  emit('update', { [key]: val })
}
const updateNumber = (key, val) => {
  emit('update', { [key]: Number(val) })
}
</script>

<style scoped>
@reference "~/assets/css/main.css";
.active .active-input{
  @apply outline-hidden border-petrol-500 ring-1 ring-petrol-500
}
.active .active-input-rues{
  @apply outline-hidden border-transparent ring-0 bg-neutral-50 rounded-none lg:rounded-xl
}
.active .custom-select-guests{
  @apply rotate-180
}
.active .active-input-searchresult{
  @apply outline-hidden border-transparent ring-0 bg-neutral-50 rounded-t-none rounded-b-xl lg:rounded-xl
}
</style>
